import './styles.scoped.css'
import {Typography, Button, IconButton, Modal, Avatar, Chip, TextField} from '@mui/material'
import {Close, DateRange, AccessTime} from '@mui/icons-material'
import {useMobile} from '../../hooks'
import {getColorFromName, getInitials, ignoreTimezone, parseError, formatTime} from '../../utils/helpers'
import {format} from 'date-fns'
import {scheduleApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {VisitDialogCardSkeleton} from '../Skeletons'

export default ({open, setOpen, loadData, items = [], loading = false}) => {
    const {mobile} = useMobile()
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                reason: yup.string(),
            }),
        ),
    })

    const submit = data => {
        setLoading(true)
        scheduleApi
            .cancelScheduleVisits(branchId, items?.[0]?.schedule_id, {visits: items?.map(item => ({visit_id: item?.id, reason: data?.reason}))})
            .then(() => {
                setLoading(false)
                setOpen(false)
                if (items?.length == 1) {
                    setMessageBox({
                        status: 'success',
                        title: 'Schedule Canceled!',
                        description: 'The caregiver and client have been notified. This visit has been removed from the schedule.',
                    })
                } else if (items?.length > 1) {
                    setMessageBox({
                        status: 'success',
                        title: `${items?.length} Schedules Canceled!`,
                        description: 'The caregiver and client have been notified. This visit has been removed from the schedule.',
                    })
                }
                loadData()
            })
            .catch(error => {
                setLoading(false)
                setOpen(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    {mobile ? (
                        <>
                            <div className="header">
                                <Typography variant="h6" color="text.primary">
                                    Are you sure you want to cancel this{items?.length > 1 ? ' ' + items?.length : ''} schedule
                                    {items?.length > 1 ? 's' : ''}?
                                </Typography>
                                <IconButton onClick={() => setOpen(false)}>
                                    <Close />
                                </IconButton>
                            </div>
                            <div className="actions">
                                <Button variant="outlined" onClick={() => setOpen(false)} style={{flex: 1}}>
                                    Keep Schedule
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    fullWidth
                                    onClick={form.handleSubmit(submit)}
                                    disabled={loading}
                                    style={{flex: 1}}>
                                    {items?.length > 1 ? `Cancel and Notify (${items?.length})` : 'Cancel and Notify'}
                                </Button>
                            </div>
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <Typography variant="h6" color="text.primary" className="title">
                            Are you sure you want to cancel this{items?.length > 1 ? ' ' + items?.length : ''} schedule
                            {items?.length > 1 ? 's' : ''}?
                        </Typography>
                    )}
                    <br />
                    <Typography variant="body2" color="text.primary" className="title">
                        Select a new date and time for this visit.
                    </Typography>
                    {items?.length == 1 &&
                        items.map((item, index) =>
                            loading ? (
                                <div key={index}>
                                    <br />
                                    <VisitDialogCardSkeleton />
                                </div>
                            ) : (
                                <div key={index}>
                                    <br />
                                    <div className="card">
                                        <div className="card-profile">
                                            <div className="people-card">
                                                {item?.schedule?.client?.avatar_url ? (
                                                    <Avatar src={item?.schedule?.client?.avatar_url} style={{width: 40, height: 40}} />
                                                ) : (
                                                    <Avatar
                                                        style={{
                                                            backgroundColor: getColorFromName(
                                                                item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name,
                                                            ),
                                                            width: 40,
                                                            height: 40,
                                                            fontSize: 20,
                                                        }}>
                                                        {getInitials(item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name)}
                                                    </Avatar>
                                                )}
                                                <div className="people-profile-card">
                                                    <Typography variant="h8" color="text.primary" className="name">
                                                        {item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name}
                                                    </Typography>
                                                    <Typography variant="body3" color="text.secondary" className="name">
                                                        {item?.schedule?.service?.name}
                                                    </Typography>
                                                </div>
                                            </div>
                                            {item?.status === 'scheduled' && (
                                                <Chip
                                                    label="Scheduled"
                                                    variant="filled"
                                                    sx={theme => ({
                                                        backgroundColor: theme.palette.grey[700],
                                                        color: theme.palette.secondary.contrastText,
                                                    })}
                                                    size="small"
                                                />
                                            )}
                                            {item?.status === 'in_progress' && (
                                                <Chip label="In progress" variant="filled" color="warning" size="small" />
                                            )}
                                            {item?.status === 'ended' && (
                                                <Chip
                                                    label="Ended"
                                                    variant="filled"
                                                    sx={theme => ({
                                                        backgroundColor: '#02CAD1',
                                                        color: theme.palette.primary.contrastText,
                                                    })}
                                                    size="small"
                                                />
                                            )}
                                            {item?.status === 'finished' && <Chip label="Finished" variant="filled" color="info" size="small" />}
                                            {item?.status === 'completed' && <Chip label="Completed" variant="filled" color="success" size="small" />}
                                            {item?.status === 'cancelled' && <Chip label="Cancelled" variant="filled" color="error" size="small" />}
                                        </div>
                                        <br />
                                        <div className="datetime">
                                            <div className="row-content">
                                                <Typography variant="body3" color="text.secondary">
                                                    <DateRange fontSize="small" />
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary">
                                                    {format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy')}
                                                </Typography>
                                            </div>
                                            <Typography variant="body3" color="text.secondary">
                                                |
                                            </Typography>
                                            <div className="row-content">
                                                <Typography variant="body3" color="text.secondary">
                                                    <AccessTime fontSize="small" />
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary">
                                                    {formatTime(item?.start_time)} - {formatTime(item?.end_time)}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ),
                        )}
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Reason
                    </Typography>
                    <TextField placeholder="Please tell us the cancellation reason..." fullWidth multiline rows={5} {...form.register('reason')} />
                    <br />
                    <br />
                    {mobile ? (
                        <>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <div className="actions">
                            <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                                Keep Schedule
                            </Button>
                            <Button variant="contained" color="error" fullWidth onClick={form.handleSubmit(submit)} disabled={loading}>
                                {items?.length > 1 ? `Cancel and Notify (${items?.length})` : 'Cancel and Notify'}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
